/* =========================================================
   Affiche PÉTANQUE — le cadre, le carreau, la partie, le concours,
   la triplette.
   API : SNPtBoules.render(data, opts) -> string SVG
     data : { mode, titre, kicker, club, lieu, joueur, equipe,
              mode 'terrain'  → s1..s3 (« valeur|libellé ») ; sinon les cotes officielles
              mode 'carreau'  → b1..b6 (« 18|N|Le carreau ») : distance en cm,
                                camp N (nous) ou A (adverse), libellé facultatif
              mode 'partie'   → score, scoreAdv, resultat, s1..s3
              mode 'concours' → p1..p8 (« Quarts|Draguignan|13-7|V »)
              mode 'triplette'→ r1..r3 (« Thomas Leroux|Tireur ») }
     opts : { palette }

   Auto-suffisant (aucun fetch, aucune image, aucun aléa) → re-render serveur
   direct par le worker Gelato.
   ========================================================= */
(function () {
  const PW = 800, PH = 1066, MX = 64;

  const PALETTES = {
    terre: { label:'Terre',  bg:'#2B2018', ink:'#F5EFE4', muted:'#B49B7E', accent:'#E8A33D', line:'#4A392A', card:'#3A2C20', aire:'#9C7B54', deep:'#6E5539' },
    craie: { label:'Craie',  bg:'#F7F4ED', ink:'#2B2018', muted:'#8B8073', accent:'#C2410C', line:'#E3DDD0', card:'#FFFFFF', aire:'#C9A87C', deep:'#8A6A46' },
    olive: { label:'Olive',  bg:'#1F2A1C', ink:'#F2F0E4', muted:'#9BAA8E', accent:'#E8A33D', line:'#33422E', card:'#2A3726', aire:'#7E8C63', deep:'#4C5738' },
    nuit:  { label:'Nuit',   bg:'#0B2D5B', ink:'#F7F4ED', muted:'#93A1B8', accent:'#FF9E5C', line:'#1E4272', card:'#12355F', aire:'#2C4C77', deep:'#082444' },
  };

  const esc = s => String(s == null ? '' : s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const up = s => String(s == null ? '' : s).toUpperCase();
  const num = s => { const m = String(s == null ? '' : s).replace(',', '.').match(/-?\d+(\.\d+)?/); return m ? parseFloat(m[0]) : null; };
  const fit = (t, maxW, maxS, cwf, ls, minS) => { const n = String(t).length || 1; minS = minS || 12;
    for (let s = maxS; s > minS; s -= 1) if (n * (cwf * s + ls) <= maxW) return s; return minS; };
  const T = (x,y,size,fill,txt,o) => { o = o || {};
    return `<text x="${x}" y="${y}" font-family="${o.f||'Sora'}" font-size="${size}" font-weight="${o.w||600}" fill="${fill}"`
      + (o.ls?` letter-spacing="${o.ls}"`:'') + ` text-anchor="${o.a||'middle'}"` + (o.op?` opacity="${o.op}"`:'') + `>${txt}</text>`; };

  const COTES = [['15 m','Longueur'], ['4 m','Largeur'], ['6-10 m','Lancer du but']];

  /* ---------------------------------------------------------------
     LA BOULE. Stries calculées comme des CORDES du cercle (demi-largeur
     √(r²−k²)) plutôt que rognées par un clipPath : resvg rend les deux, mais
     le calcul direct ne dépend d'aucune fonctionnalité optionnelle du moteur
     de rendu — et le fichier print passe par resvg, pas par un navigateur.
     --------------------------------------------------------------- */
  function boule(cx, cy, r, fill, trait, opacite) {
    let g = `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="${r.toFixed(1)}" fill="${fill}"`
      + (trait ? ` stroke="${trait}" stroke-width="${Math.max(1.5, r*0.10).toFixed(1)}"` : '')
      + (opacite ? ` opacity="${opacite}"` : '') + `/>`;
    for (const k of [-0.56, -0.28, 0, 0.28, 0.56]) {
      const y = cy + k * r, hw = Math.sqrt(Math.max(0, 1 - k*k)) * r * 0.88;
      g += `<line x1="${(cx-hw).toFixed(1)}" y1="${y.toFixed(1)}" x2="${(cx+hw).toFixed(1)}" y2="${y.toFixed(1)}"`
        + ` stroke="${trait || '#000000'}" stroke-width="${Math.max(0.8, r*0.055).toFixed(1)}" opacity="${(opacite || 1) * 0.45}"/>`;
    }
    return g;
  }

  /* ---------------------------------------------------------------
     LE CADRE, aux cotes FFPJP. Terrain de compétition 15 × 4 m.
       cercle de lancer   Ø 0,50 intérieur, tracé à 1,50 de la ligne de fond
       lancer du but      entre 6 et 10 m du cercle, à 1 m mini de toute limite
       but (cochonnet)    Ø 0,030 · boule Ø 0,0705 à 0,080

     LE BUT N'EST PAS À L'ÉCHELLE, et c'est assumé : 3 cm sur un cadre de 15 m
     ne feraient pas un pixel. Il est donc dessiné comme un REPÈRE DE POSITION,
     à un rayon minimum lisible. Les boules, elles, ne sont pas dessinées ici du
     tout — le mode « carreau » leur donne une échelle explicite en centimètres
     plutôt qu'un rapport faux.
     --------------------------------------------------------------- */
  function cadre(cx, cy, hauteur, p) {
    const H = hauteur, W = H * (4 / 15);
    const X = cx - W/2, Y = cy - H/2;
    const m = H / 15;                          /* un mètre, en unités SVG */
    const L = '#FFFFFF', sw = Math.max(2, W * 0.022), fin = sw * 0.7;
    const fy = d => Y + H - d * m;             /* mètres depuis la ligne de fond basse */

    let g = `<rect x="${X.toFixed(1)}" y="${Y.toFixed(1)}" width="${W.toFixed(1)}" height="${H.toFixed(1)}" fill="${p.aire}"/>`;

    /* Gravier : semis déterministe (suite du nombre d'or, aucun aléa) pour que
       la surface se lise comme de la terre battue et non comme un parquet. */
    for (let i = 0; i < 150; i++) {
      const a = (i * 0.6180339887) % 1, b = (i * 0.7548776662) % 1;
      const rr = 0.9 + ((i * 0.4114) % 1) * 1.5;
      g += `<circle cx="${(X + a*W).toFixed(1)}" cy="${(Y + b*H).toFixed(1)}" r="${rr.toFixed(1)}" fill="${p.deep}" opacity="0.34"/>`;
    }

    /* LES PETITS TRAITS : une graduation au mètre le long des deux côtés.
       C'est elle qui donne l'échelle sans écrire une seule cote. */
    for (let d = 1; d < 15; d++) {
      const yy = fy(d), lg = (d % 5 === 0 ? 0.44 : 0.24) * m;
      for (const [xx, dir] of [[X, 1], [X + W, -1]])
        g += `<line x1="${xx.toFixed(1)}" y1="${yy.toFixed(1)}" x2="${(xx + dir*lg).toFixed(1)}" y2="${yy.toFixed(1)}"`
          + ` stroke="${L}" stroke-width="${fin}" opacity="${d % 5 === 0 ? 0.95 : 0.5}"/>`;
    }

    /* Cercle de lancer, à 1,50 m de la ligne de fond, Ø 0,50. */
    const yC = fy(1.5);
    g += `<circle cx="${cx.toFixed(1)}" cy="${yC.toFixed(1)}" r="${(0.25*m).toFixed(1)}" fill="none" stroke="${L}" stroke-width="${sw}"/>`;

    /* LA FENÊTRE DE LANCER : le but est valide entre 6 et 10 m du cercle. Deux
       arcs en pointillé matérialisent cette bande — c'est la règle la plus
       spécifique du jeu, et elle ne figure sur aucun plan de terrain. */
    for (const d of [6, 10]) {
      const yy = fy(1.5 + d);
      g += `<line x1="${X.toFixed(1)}" y1="${yy.toFixed(1)}" x2="${(X+W).toFixed(1)}" y2="${yy.toFixed(1)}"`
        + ` stroke="${L}" stroke-width="${fin}" stroke-dasharray="${(W*0.09).toFixed(1)} ${(W*0.07).toFixed(1)}" opacity="0.8"/>`;
    }
    /* Le but, posé à 8 m — repère de position, rayon minimum lisible. */
    g += `<circle cx="${cx.toFixed(1)}" cy="${fy(9.5).toFixed(1)}" r="${Math.max(3.2, 0.09*m).toFixed(1)}" fill="${p.accent}"/>`;

    /* bordure tracée EN DEDANS : sur fond clair, la moitié extérieure d'un
       trait blanc centré se perdrait dans le fond */
    g += `<rect x="${(X + sw/2).toFixed(1)}" y="${(Y + sw/2).toFixed(1)}" width="${(W - sw).toFixed(1)}" height="${(H - sw).toFixed(1)}" fill="none" stroke="${L}" stroke-width="${sw}"/>`;

    /* ------------------------------------------------------------
       LES ANNOTATIONS. Un cadre de pétanque fait 15 × 4 : dessiné à sa vraie
       proportion il ne peut occuper qu'un bandeau étroit, et laisse deux
       colonnes vides. Plutôt que de déformer le terrain pour remplir l'affiche
       — ce qui serait faux — on transforme ces colonnes en planche technique
       cotée. La contrainte de proportion devient le parti pris graphique.
       ------------------------------------------------------------ */
    const note = (cote, y, titre, detail) => {
      const g2 = cote < 0 ? X : X + W, tx = g2 + cote * 16;
      return `<line x1="${g2.toFixed(1)}" y1="${y.toFixed(1)}" x2="${tx.toFixed(1)}" y2="${y.toFixed(1)}" stroke="${p.muted}" stroke-width="1.2"/>`
        + T(tx + cote * 8, y + (detail ? -3 : 5), 15, p.ink, titre, { f:'Oswald', w:700, a: cote < 0 ? 'end' : 'start' })
        + (detail ? T(tx + cote * 8, y + 15, 10.5, p.muted, detail, { w:600, ls:2, a: cote < 0 ? 'end' : 'start' }) : '');
    };
    g += note(-1, fy(1.5), 'CERCLE DE LANCER', 'DIAMÈTRE 50 CM')
       + note(-1, fy(9.5), 'LE BUT', 'À 8 M DU CERCLE')
       + note(1, fy(7.5), '6 M', 'DISTANCE MINIMALE')
       + note(1, fy(11.5), '10 M', 'DISTANCE MAXIMALE');

    /* Cote d'ensemble, à droite : deux segments et un vide pour le texte. */
    const dx = X + W + 190, my = Y + H/2;
    g += `<line x1="${dx}" y1="${Y.toFixed(1)}" x2="${dx}" y2="${(my-20).toFixed(1)}" stroke="${p.muted}" stroke-width="1.2"/>`
      + `<line x1="${dx}" y1="${(my+20).toFixed(1)}" x2="${dx}" y2="${(Y+H).toFixed(1)}" stroke="${p.muted}" stroke-width="1.2"/>`
      + T(dx, my + 6, 17, p.muted, '15 M', { f:'Oswald', w:700 });
    for (const yy of [Y, Y + H])
      g += `<line x1="${(dx-7)}" y1="${yy.toFixed(1)}" x2="${(dx+7)}" y2="${yy.toFixed(1)}" stroke="${p.muted}" stroke-width="1.2"/>`;
    return g;
  }

  /* ---------------------------------------------------------------
     LE CARREAU. Le but au centre, des anneaux gradués en centimètres, et les
     boules posées à leur distance réelle. L'ANGLE de chaque boule est
     déterministe (angle d'or, 137,5°) : deux boules voisines ne se recouvrent
     jamais, et le même formulaire redonne toujours la même affiche — condition
     absolue pour que le fichier print corresponde à l'aperçu.
     --------------------------------------------------------------- */
  function mesureBoules(data) {
    const l = [];
    for (let i = 1; i <= 6; i++) {
      const raw = String(data['b' + i] || '').trim(); if (!raw) continue;
      const c = raw.split('|').map(s => (s || '').trim());
      const d = num(c[0]); if (d == null || d < 0) continue;
      l.push({ d, nous: !/^a/i.test(c[1] || 'N'), lab: c[2] || '' });
    }
    return l;
  }
  function pointsDeLaMene(l) {
    const adv = l.filter(b => !b.nous).map(b => b.d);
    const seuil = adv.length ? Math.min.apply(null, adv) : Infinity;
    return l.filter(b => b.nous && b.d < seuil).length;
  }
  function carreau(cx, cy, R, p, l) {
    /* Échelle : un pas rond qui englobe la boule la plus lointaine. */
    const maxD = l.length ? Math.max.apply(null, l.map(b => b.d)) : 40;
    const pas = maxD <= 24 ? 5 : maxD <= 48 ? 10 : maxD <= 100 ? 20 : Math.ceil(maxD / 4 / 10) * 10;
    const n = Math.max(2, Math.min(5, Math.ceil(maxD / pas)));
    const portee = pas * n;
    /* UNE SEULE ÉCHELLE pour les anneaux ET pour les boules : une boule à
       20 cm tombe exactement sur l'anneau des 20 cm. Sans cette égalité le
       dessin mentirait, et c'est tout ce que cette affiche a à raconter.

       L'échelle est en RACINE CARRÉE, et non proportionnelle. Une mène va
       couramment de 4 cm à 60 cm : en linéaire, les trois boules qui se
       disputent le point s'empilent sur quinze pixels au centre pendant que la
       boule perdue occupe toute l'affiche — l'inverse de ce qu'il faut
       montrer. La racine dilate le près, resserre le loin, et les anneaux
       resserrés vers l'extérieur se lisent comme un radar. C'est aussi la
       projection qui conserve les aires. */
    const rayon = d => R * Math.sqrt(Math.max(0, d) / portee);

    let g = '';
    for (let i = n; i >= 1; i--) {
      const r = rayon(i * pas);
      g += `<circle cx="${cx}" cy="${cy.toFixed(1)}" r="${r.toFixed(1)}" fill="${i === n ? p.card : 'none'}"`
        + ` stroke="${p.line}" stroke-width="${i === n ? 2.5 : 1.5}"${i < n ? ' stroke-dasharray="6 7"' : ''}/>`;
    }
    /* Les cotes des anneaux courent le long d'UNE SEULE diagonale, en haut à
       gauche. Les poser à l'horizontale les empilait au même endroit ; sur une
       diagonale réservée, elles s'échelonnent naturellement — et le secteur des
       boules, plus bas, est calculé pour ne jamais l'atteindre. */
    const aCote = 233 * Math.PI / 180;
    for (let i = 1; i <= n; i++) {
      const r = rayon(i * pas) - 13;
      g += T(cx + r * Math.cos(aCote), cy + r * Math.sin(aCote) + 4, 11.5, p.muted, `${i * pas} CM`, { w:600, ls:1.4 });
    }
    for (const [x1,y1,x2,y2] of [[cx-R,cy,cx+R,cy], [cx,cy-R,cx,cy+R]])
      g += `<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" stroke="${p.line}" stroke-width="1" opacity="0.5"/>`;

    /* Les boules occupent l'arc −45° → 195°, jamais la diagonale des cotes.
       On les dessine de la plus lointaine à la plus proche pour que la boule
       du point reste au-dessus si la grappe est serrée autour du but. */
    const gagnante = Math.min.apply(null, l.map(b => b.d));
    const rb = l.length > 4 ? 18 : 21;
    const ordre = l.map((b, i) => ({ b, i })).sort((u, v) => v.b.d - u.b.d);
    for (const { b, i } of ordre) {
      const a = ((l.length > 1 ? -45 + i * (240 / (l.length - 1)) : 60)) * Math.PI / 180;
      const rr = rayon(Math.min(b.d, portee));
      const x = cx + rr * Math.cos(a), y = cy + rr * Math.sin(a);
      /* Stries et contour tirés du FOND : quelle que soit la palette, ils
         contrastent avec l'aplat de la boule sans introduire de cinquième
         couleur. */
      g += boule(x, y, rb, b.nous ? p.accent : p.card, b.nous ? p.bg : p.muted);
      if (b.d === gagnante) g += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${(rb + 7).toFixed(1)}" fill="none" stroke="${p.accent}" stroke-width="2.5"/>`;
      const rl = rr + rb + 17, lx = cx + rl * Math.cos(a), ly = cy + rl * Math.sin(a);
      const txt = b.lab ? `${b.lab} · ${b.d} cm` : `${b.d} cm`;
      g += T(lx, ly + 5, fit(txt, 190, 13.5, 0.58, 0, 10), p.ink, esc(up(txt)), { f:'Oswald', w:700 });
    }
    /* Le but, dessiné en dernier : il reste visible même sous une grappe
       serrée. Encré plutôt que blanc — sur la palette craie, un but blanc sur
       disque blanc disparaissait purement et simplement. */
    g += `<circle cx="${cx}" cy="${cy.toFixed(1)}" r="9" fill="${p.ink}" stroke="${p.card}" stroke-width="2"/>`;
    g += T(cx, cy + R + 44, 11.5, p.muted, `LE BUT AU CENTRE · UN ANNEAU TOUS LES ${pas} CM`, { w:600, ls:2.6 });
    return g;
  }

  function stats(p, items, y) {
    const it = (items || []).filter(x => x && String(x[0] || '').trim());
    if (!it.length) return '';
    const x0 = 54, w = (PW - 2*x0) / it.length;
    let s = `<line x1="${x0}" y1="${y-34}" x2="${PW-x0}" y2="${y-34}" stroke="${p.line}" stroke-width="1.5"/>`;
    it.forEach((c, i) => {
      const cc = x0 + w*i + w/2;
      if (i) s += `<line x1="${(x0+w*i).toFixed(0)}" y1="${y-14}" x2="${(x0+w*i).toFixed(0)}" y2="${y+34}" stroke="${p.line}" stroke-width="1.5"/>`;
      s += T(cc, y+6, fit(c[0], w-24, 38, 0.56, 0, 17), p.ink, esc(c[0]), { f:'Oswald', w:700 })
        + T(cc, y+30, 12, p.muted, esc(up(c[1])), { w:600, ls:2.5 });
    });
    return s;
  }
  function troisValeurs(p, data, y, defaut) {
    const perso = [1, 2, 3].map(i => {
      const raw = String(data['s' + i] || '').trim(); if (!raw) return null;
      const [v, l] = raw.split('|').map(s => (s || '').trim());
      return v ? [v, l || ''] : null;
    });
    return stats(p, perso.some(Boolean) ? perso.filter(Boolean) : (defaut || []), y);
  }

  /* Filigrane : une boule striée, à peine visible, derrière les listes. */
  function filigrane(p) {
    const cx = 400, cy = 566, R = 208;
    let g = `<circle cx="${cx}" cy="${cy}" r="${R}" fill="${p.ink}"/>`;
    for (const k of [-0.56, -0.28, 0, 0.28, 0.56]) {
      const y = cy + k * R, hw = Math.sqrt(Math.max(0, 1 - k * k)) * R * 0.88;
      g += `<line x1="${(cx - hw).toFixed(1)}" y1="${y.toFixed(1)}" x2="${(cx + hw).toFixed(1)}" y2="${y.toFixed(1)}" stroke="${p.bg}" stroke-width="${(R * 0.055).toFixed(1)}"/>`;
    }
    return `<g opacity="0.055">${g}</g>`;
  }

  function render(data, opts) {
    data = data || {}; opts = opts || {};
    const p = PALETTES[opts.palette] || PALETTES.terre;
    const mode = String(data.mode || 'terrain').toLowerCase();
    const cx = PW / 2;

    const DEF = { terrain: 'LE CADRE', carreau: 'LE CARREAU', partie: 'LA PARTIE', concours: 'LE CONCOURS', triplette: 'LA TRIPLETTE' };
    const titre = up(data.titre) || DEF[mode] || 'LE CADRE';
    const ts = fit(titre, 690, 74, 0.54, 2, 34);
    const kicker = up(data.kicker) || up(data.club) || 'PÉTANQUE';
    const sous = [up(data.club), up(data.lieu)].filter(Boolean).join(' · ');

    let corps = '', barre = '', pied = 0;

    if (mode === 'terrain') {
      corps = cadre(cx, 596, 660, p);
      barre = troisValeurs(p, data, 968, COTES);
      pied = 1030;

    } else if (mode === 'carreau') {
      const l = mesureBoules(data);
      if (l.length) {
        corps = carreau(cx, 566, 236, p, l);
        /* On n'annonce le nombre de points QUE si l'adversaire est représenté :
           sans boule adverse, « 4 points » serait une affirmation que les
           données ne portent pas. Le pied de l'affiche ne se vide pas pour
           autant — il bascule sur deux constats que les données portent
           toujours, ce qui évite d'ajouter au formulaire un champ dont le
           client ne saurait pas s'il sera affiché ou non. */
        if (l.some(b => !b.nous)) {
          const pts = pointsDeLaMene(l);
          corps += T(cx, 918, 13, p.accent, 'LA MÈNE', { w:700, ls:8 })
            + T(cx, 968, 46, p.ink, String(pts) + (pts > 1 ? ' POINTS' : ' POINT'), { f:'Oswald', w:700, ls:1 });
          pied = 1026;
        } else {
          const proche = Math.min.apply(null, l.map(b => b.d));
          barre = stats(p, [[String(l.length), l.length > 1 ? 'Boules jouées' : 'Boule jouée'], [proche + ' cm', 'La plus près du but']], 962);
          pied = 1030;
        }
      } else {
        corps = T(cx, 600, 20, p.muted, 'FORMAT : LA DISTANCE EN CM, N OU A, UN LIBELLÉ', { w:600, ls:1.5 });
        pied = 1000;
      }

    } else if (mode === 'partie') {
      const mien = num(data.score), adv = num(data.scoreAdv);
      const gagne = mien != null && adv != null && mien > adv;
      corps = `<rect x="${MX}" y="268" width="${PW-2*MX}" height="122" rx="14" fill="${p.ink}"/>`
        + T(cx, 308, 13, p.accent, esc(up(data.resultat)) || (gagne ? 'VICTOIRE' : ''), { w:700, ls:8 })
        + `<text x="${cx}" y="362" text-anchor="middle" font-family="Oswald" font-weight="700" fill="${p.bg}"><tspan font-size="54">${esc(up(data.score)) || '—'}</tspan><tspan font-size="26" fill="${p.muted}" dx="12">–</tspan><tspan font-size="54" dx="12">${esc(up(data.scoreAdv)) || '—'}</tspan></text>`;

      /* LES TREIZE POINTS. Une partie se joue en 13 : le score n'est pas un
         nombre abstrait mais un chemin parcouru. Deux rangées de jalons le
         montrent d'un coup d'œil — et si la formule se joue en 11 ou en 15, la
         rangée s'allonge sur le score le plus haut plutôt que d'imposer 13. */
      if (mien != null && adv != null) {
        const cible = Math.max(13, Math.round(mien), Math.round(adv));
        const camps = [[esc(up(data.club)) || 'NOUS', Math.round(mien), p.accent],
                       [esc(up(data.adversaire)) || 'ADVERSAIRE', Math.round(adv), p.muted]];
        camps.forEach(([nom, n, teinte], k) => {
          const y = 500 + k * 108;
          corps += T(MX, y, 13, p.muted, nom, { w:600, ls:3.5, a:'start' })
            + T(PW - MX, y + 4, 26, teinte, String(n), { f:'Oswald', w:700, a:'end' });
          const pasX = Math.min(30, 520 / cible), r = Math.min(11, pasX * 0.40);
          const x0 = cx - (cible - 1) * pasX / 2;
          for (let i = 0; i < cible; i++) {
            const x = x0 + i * pasX;
            corps += i < n
              ? `<circle cx="${x.toFixed(1)}" cy="${y + 38}" r="${r.toFixed(1)}" fill="${teinte}"/>`
              : `<circle cx="${x.toFixed(1)}" cy="${y + 38}" r="${(r - 1).toFixed(1)}" fill="none" stroke="${p.line}" stroke-width="2"/>`;
          }
        });
      }
      /* Trois boules et le but, en bas : ornement assumé, aucune donnée du
         client n'y est représentée. */
      corps += boule(cx - 92, 802, 54, p.card, p.muted)
        + boule(cx + 78, 782, 64, p.accent, p.bg)
        + boule(cx - 6, 860, 44, p.card, p.muted)
        + `<circle cx="${cx + 4}" cy="${716}" r="13" fill="${p.ink}"/>`;
      barre = troisValeurs(p, data, 966, null);
      pied = 1028;

    } else if (mode === 'concours') {
      const l = [];
      for (let i = 1; i <= 8; i++) {
        const raw = String(data['p' + i] || '').trim(); if (!raw) continue;
        const c = raw.split('|').map(s => (s || '').trim());
        if (c[1]) l.push(c);
      }
      if (l.length) {
        const zone = 620, pas = Math.min(102, zone / l.length), y0 = 340 + (zone - pas*l.length) / 2;
        l.forEach((r, i) => {
          const y = y0 + i * pas, v = /^v/i.test(r[3] || '');
          corps += (r[0] ? T(MX, y - 12, 13, p.muted, esc(up(r[0])), { w:600, ls:2, a:'start' }) : '')
            + T(MX, y + 16, fit(r[1], 400, 26, 0.60, 0, 15), p.ink, esc(r[1]), { f:'Oswald', w:700, a:'start' })
            + (r[2] ? T(PW - MX - 62, y + 16, 30, v ? p.accent : p.muted, esc(r[2]), { f:'Oswald', w:700, a:'end' }) : '')
            + (r[3] ? T(PW - MX, y + 14, 20, v ? p.accent : p.muted, esc(up(r[3])), { f:'Oswald', w:700, a:'end' }) : '');
          if (i < l.length - 1) corps += `<line x1="${MX}" y1="${(y + pas/2 - 7).toFixed(1)}" x2="${PW-MX}" y2="${(y + pas/2 - 7).toFixed(1)}" stroke="${p.line}" stroke-width="1.5"/>`;
        });
      } else corps = T(cx, 600, 20, p.muted, 'FORMAT : TOUR, ADVERSAIRE, SCORE, V OU D', { w:600, ls:2 });
      pied = 1000;

    } else { /* triplette */
      const j = [];
      for (let i = 1; i <= 3; i++) {
        const raw = String(data['r' + i] || '').trim(); if (!raw) continue;
        const [nom, role] = raw.split('|').map(s => (s || '').trim());
        if (nom) j.push([nom, role || '']);
      }
      if (j.length) {
        const bh = Math.min(196, 604 / j.length), y0 = 322 + (604 - bh*j.length) / 2;
        j.forEach((r, i) => {
          const y = y0 + i * bh, cyB = y + bh/2;
          corps += `<rect x="${MX}" y="${y.toFixed(1)}" width="${PW-2*MX}" height="${(bh-14).toFixed(1)}" rx="16" fill="${p.card}"/>`
            + boule(MX + 76, cyB - 7, Math.min(46, bh*0.26), p.accent, p.deep)
            + T(MX + 152, cyB - 12, fit(r[0], 420, 34, 0.58, 0, 17), p.ink, esc(up(r[0])), { f:'Oswald', w:700, a:'start' })
            + (r[1] ? T(MX + 152, cyB + 20, 14, p.muted, esc(up(r[1])), { w:600, ls:3.5, a:'start' }) : '');
        });
      } else corps = T(cx, 600, 20, p.muted, 'FORMAT : LE NOM, UNE BARRE VERTICALE, LE RÔLE', { w:600, ls:1.5 });
      pied = 990;
    }

    /* Le filigrane n'est posé que sur les modes où la place est vraiment
       vide : là où un dessin occupe déjà l'affiche, il ne ferait que salir. */
    const fili = ["concours"].indexOf(mode) >= 0 ? filigrane(p) : '';
    const bas = [esc(up(data.joueur)), esc(up(data.equipe))].filter(Boolean).join(' · ');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${PW} ${PH}" font-family="'Sora','Inter',sans-serif">
<rect width="${PW}" height="${PH}" fill="${p.bg}"/>
${T(cx, 84, 19, p.accent, esc(kicker), { w:700, ls:5 })}
${T(cx, 168, ts, p.ink, esc(titre), { f:'Oswald', w:700, ls:2 })}
${sous ? T(cx, 204, fit(sous, 660, 17, 0.66, 3.5, 11), p.muted, esc(sous), { w:600, ls:3.5 }) : ''}
${fili}
${corps}
${barre}
${pied && bas ? T(cx, pied, fit(bas, 690, 19, 0.60, 1, 12), p.ink, bas, { f:'Oswald', w:700, ls:1 }) : ''}
</svg>`;
  }

  window.SNPtBoules = { render, PALETTES, COTES };
})();
